/**
 * FoodPulseTopNav — sticky top navigation for the FoodPulse home.
 *
 * Layout matches the design spec:
 *   [🌿 FoodPulse.net]  Discover · Daily Deals · Food Guide · Farms & Stores · Reduce & Share · Community
 *                       [🔍]  [📍 City, ST ▾]  [Sign In]  [Join FoodPulse]
 *
 * The location pill opens a cascading Country → State → City selector.
 * On first mount it auto-detects the user's coords via browser geolocation
 * and reverse-geocodes them so the pill starts populated. Manual overrides
 * are persisted through the shared FoodPulseLocation store (`fp_user_location`)
 * so the rest of the FoodPulse app picks up the same locality.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, ChevronDown, Locate, X, Check, Loader2, Download } from "lucide-react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "fp_user_location";
const LEGACY_KEY = "fp:userLocation";

function readStored() {
  try {
    const cur = localStorage.getItem(STORAGE_KEY);
    if (cur) return JSON.parse(cur);
    const legacy = localStorage.getItem(LEGACY_KEY);
    return legacy ? JSON.parse(legacy) : null;
  } catch { return null; }
}

function writeStored(loc) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    // Keep the legacy key in sync so <LocalDealsHero /> keeps working.
    localStorage.setItem(LEGACY_KEY, JSON.stringify(loc));
    window.dispatchEvent(new CustomEvent("fp:location:change", { detail: loc }));
  } catch { /* non-fatal */ }
}

const NAV_LINKS = [
  { to: "/food-pulse/journeys/discover",   label: "Discover" },
  { to: "/food-pulse/deals",               label: "Daily Deals" },
  { to: "/food-pulse/guides",              label: "Food Guide" },
  { to: "/food-pulse/businesses",          label: "Farms & Stores" },
  { to: "/food-pulse/journeys/share",      label: "Reduce & Share" },
  { to: "/food-pulse/articles",            label: "Community" },
];

export default function FoodPulseTopNav() {
  const { user } = useAuth();
  const [loc, setLoc] = useState(readStored);
  const [pickerOpen, setPickerOpen] = useState(false);

  // On first mount — if we have nothing stored, try browser geolocation
  // + reverse-geocode so the pill starts populated instead of empty.
  useEffect(() => {
    if (loc) return;
    if (!("geolocation" in navigator)) return;
    let alive = true;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await http.get(`/geocode/reverse?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
          if (alive && data?.city && data?.country) {
            const next = {
              city: data.city, state: data.state || "", country: data.country,
              country_code: data.country_code || "", lat: pos.coords.latitude, lng: pos.coords.longitude,
            };
            setLoc(next);
            writeStored(next);
          }
        } catch { /* non-fatal */ }
      },
      () => { /* denied — user picks manually */ },
      { timeout: 6000, maximumAge: 5 * 60 * 1000 },
    );
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const pretty = useMemo(() => {
    if (!loc) return "Set location";
    const parts = [loc.city, loc.state && stateAbbrev(loc.state, loc.country_code)].filter(Boolean);
    return parts.length ? parts.join(", ") : "Set location";
  }, [loc]);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur" data-testid="fp-top-nav">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4">
        <Link to="/food-pulse" data-testid="fp-nav-logo" className="flex items-center gap-2.5 font-bold">
          <img
            src="/foodpulse-logo-round.png"
            alt="FoodPulse"
            width="40"
            height="40"
            className="h-10 w-10 rounded-full ring-1 ring-emerald-100"
            loading="eager"
            decoding="async"
          />
          <span className="text-lg tracking-tight">
            <span className="text-emerald-700">Food</span><span className="text-emerald-900">Pulse</span><sup className="text-[10px] text-slate-400 relative -top-1">™</sup><span className="text-emerald-600">.net</span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center gap-6 text-sm text-slate-700 lg:flex">
          {NAV_LINKS.map((n) => (
            <Link
              key={n.to + n.label}
              to={n.to}
              data-testid={`fp-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="hover:text-emerald-700"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/food-pulse/food"
            data-testid="fp-nav-search"
            aria-label="Search"
            className="grid h-9 w-9 place-content-center rounded-full border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50"
          >
            <Search className="h-4 w-4 text-slate-500" />
          </Link>

          <button
            onClick={() => setPickerOpen(true)}
            data-testid="fp-nav-location"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 px-3 text-sm text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
          >
            <MapPin className="h-4 w-4 text-emerald-600" />
            <span className="max-w-[160px] truncate">{pretty}</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>

          <Link
            to="/install"
            data-testid="fp-nav-install"
            aria-label="Install App"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 hidden sm:inline-flex"
          >
            <Download className="h-3.5 w-3.5" />
            Get App
          </Link>

          {!user ? (
            <>
              <Link to="/login?next=/food-pulse" data-testid="fp-nav-signin">
                <Button variant="outline" className="h-9 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  Sign In
                </Button>
              </Link>
              <Link to="/register?next=/food-pulse" data-testid="fp-nav-join">
                <Button className="h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">Join FoodPulse</Button>
              </Link>
            </>
          ) : (
            <Link to="/account" data-testid="fp-nav-account">
              <Button variant="outline" className="h-9 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                {(user.name || user.email || "You").split(" ")[0]}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {pickerOpen && (
        <LocationPickerModal
          initial={loc}
          onClose={() => setPickerOpen(false)}
          onSave={(next) => { setLoc(next); writeStored(next); setPickerOpen(false); }}
        />
      )}
    </header>
  );
}

function LocationPickerModal({ initial, onClose, onSave }) {
  const [country, setCountry] = useState(initial?.country_code || "US");
  const [state, setState] = useState(initial?.state || "");
  const [city, setCity] = useState(initial?.city || "");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await http.get("/geo/countries");
        setCountries(r.data?.countries || []);
      } catch { setCountries([]); }
    })();
  }, []);

  useEffect(() => {
    setState(""); setCity("");
    (async () => {
      try {
        const r = await http.get("/geo/subdivisions", { params: { country } });
        setStates(r.data?.subdivisions || []);
      } catch { setStates([]); }
    })();
  }, [country]);

  // City autocomplete against /geocode/city-suggest.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!city || city.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await http.get("/geocode/city-suggest", { params: { q: city, country } });
        setSuggestions(r.data?.items || r.data?.suggestions || []);
      } catch { setSuggestions([]); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [city, country]);

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) return;
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await http.get(`/geocode/reverse?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
          if (data?.city && data?.country) {
            onSave({
              city: data.city, state: data.state || "", country: data.country,
              country_code: data.country_code || "", lat: pos.coords.latitude, lng: pos.coords.longitude,
            });
          }
        } catch { /* ignore */ }
        setBusy(false);
      },
      () => setBusy(false),
      { timeout: 8000 },
    );
  };

  const save = () => {
    if (!city.trim() || !country) return;
    const countryName = countries.find((c) => c.code === country)?.name || country;
    onSave({ city: city.trim(), state: state.trim(), country: countryName, country_code: country });
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-start justify-center bg-black/40 p-4 pt-24" data-testid="fp-location-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-600">Set your location</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-900">Where do you eat?</div>
          </div>
          <button onClick={onClose} data-testid="fp-location-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <button
          onClick={useMyLocation}
          disabled={busy}
          data-testid="fp-location-use-gps"
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />} Use my current location
        </button>

        <div className="mt-4 space-y-3">
          <label className="block text-xs text-slate-600">Country</label>
          <select
            data-testid="fp-location-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
          >
            {countries.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
          </select>

          <label className="block text-xs text-slate-600">State / Province</label>
          <select
            data-testid="fp-location-state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
          >
            <option value="">— None / All —</option>
            {states.map((s) => (<option key={s.code} value={s.name}>{s.name}</option>))}
          </select>

          <label className="block text-xs text-slate-600">City / Village</label>
          <div className="relative">
            <input
              data-testid="fp-location-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Start typing…"
              className="w-full rounded-md border border-slate-200 px-2 py-2 text-sm"
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                {suggestions.slice(0, 8).map((s, i) => (
                  <li key={`${s.name || s.city}-${i}`}>
                    <button
                      onClick={() => { setCity(s.name || s.city); setSuggestions([]); if (s.state) setState(s.state); }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-emerald-50"
                    >
                      <MapPin className="h-3 w-3 text-slate-400" /> {s.name || s.city}{s.state ? `, ${s.state}` : ""}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            data-testid="fp-location-save"
            disabled={!city.trim() || !country}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Check className="mr-1 h-4 w-4" /> Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function stateAbbrev(name, cc) {
  // Very small local table for the two most-common FoodPulse markets.
  if (cc === "US") {
    const US = {
      "Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA","Colorado":"CO","Connecticut":"CT",
      "Delaware":"DE","Florida":"FL","Georgia":"GA","Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN",
      "Iowa":"IA","Kansas":"KS","Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD","Massachusetts":"MA",
      "Michigan":"MI","Minnesota":"MN","Mississippi":"MS","Missouri":"MO","Montana":"MT","Nebraska":"NE","Nevada":"NV",
      "New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND",
      "Ohio":"OH","Oklahoma":"OK","Oregon":"OR","Pennsylvania":"PA","Rhode Island":"RI","South Carolina":"SC",
      "South Dakota":"SD","Tennessee":"TN","Texas":"TX","Utah":"UT","Vermont":"VT","Virginia":"VA","Washington":"WA",
      "West Virginia":"WV","Wisconsin":"WI","Wyoming":"WY",
    };
    return US[name] || name;
  }
  return name;
}
